"use client";

import { GenericDialog } from "@molecules/GenericDialog";
import { Box, CircularProgress } from "@mui/material";
import { UserAccountProvider } from "@shared/contexts/UserAccountContext";
import { signout } from "@shared/handler/CommonHandler";
import { useAuth } from "@shared/hooks/useAuth";
import { type ReactNode, useEffect, useState } from "react";

type Props = {
  children: ReactNode;
};

export const AuthGuard = ({ children }: Props) => {
  const { userAccount, isAuthenticated, isLoading, invalidUserAccount } =
    useAuth();
  const [open, setOpen] = useState(true);

  // 未認証または無効なユーザーアカウントの場合はサインアウト
  useEffect(() => {
    // ローディング完了かつ未認証の場合のみリダイレクト
    if ((!isLoading && !isAuthenticated) || invalidUserAccount) {
      signout();
    }
  }, [isAuthenticated, isLoading, invalidUserAccount]);

  // ローディング中の表示
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // 未認証の場合は何も表示しない（リダイレクト処理中）
  if (!isAuthenticated) {
    return null;
  }

  return (
    <UserAccountProvider value={userAccount}>
      {children}
      {userAccount.isUnlinkedOrganization() && (
        <GenericDialog
          open={open}
          onClose={() => setOpen(false)}
          title="ログインは正常に完了しましたが、まだご利用になれません"
          message="ログインは正常に完了しましたが、管理者があなたの所属を設定していません。しばらくお待ちいただくか、あなたの組織の管理者にお問い合わせください。"
        />
      )}
    </UserAccountProvider>
  );
};
